import React, { Fragment, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { getLinkProps, getLinkTagName } from 'Components/utils/markup/markup';
import ratios from 'Components/utils/ratios/ratios.utils';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { t } from 'Src/i18n';
import type { LabeledIcon } from 'Components/frameworks/labeled-icons-list/LabeledIconsList';
import 'Components/frameworks/labeled-icons-list/LabeledIconsList';
import { TeaserCard } from 'Components/frameworks/teaser-card/TeaserCard';
import { Title } from 'Components/frameworks/title/Title';
export type ProductRoomCardProps = {
  className?: string;
  isExclusiveCollection?: boolean;
  isHidden?: boolean;
  isLarge?: boolean;
  labeledIconsList?: Array<LabeledIcon>;
  link?: {
    onClick?: () => any;
    target?: string;
    url?: string;
  };
  media: Array<{
    alt?: string;
    onClick?: () => void;
    src: string;
  }>;
  seeMoreLabel?: string;
  title: string;
};
export const ProductRoomCard = ({
  className,
  isHidden,
  isLarge,
  labeledIconsList = [],
  link = {},
  media = [],
  seeMoreLabel,
  title,
}: ProductRoomCardProps) => {
  const [isHovering, setHovering] = useState(false);
  const { isDesktop, isTablet } = useResponsive();
  const TagName = getLinkTagName(link);
  const linkProps = getLinkProps(link);
  const content = (
    <Fragment>
      <Title hasEllipsis theme="title-brand-n3">
        {title}
      </Title>
      <ul className="space-y-5">
        {labeledIconsList.map(
          ({ labels }, index) =>
            !!labels && (
              <li
                className="inline-flex min-h-20 w-full items-center justify-between gap-5 overflow-hidden text-14"
                key={index}
              >
                {labels.map((label, labelIndex) => (
                  <span
                    className={classnames({
                      'text-mediumGrey': labelIndex === 0,
                      'text-end font-bold text-black': labelIndex !== 0,
                    })}
                    key={label}
                  >
                    {label}
                    &nbsp;
                  </span>
                ))}
              </li>
            ),
        )}
      </ul>
      {!!seeMoreLabel && (
        <p className="mt-auto text-center text-12 text-black underline">{seeMoreLabel}</p>
      )}
    </Fragment>
  );
  const props = {
    ...linkProps,
    onClick: () => {
      linkProps.onClick?.();
    },
  };
  const ratio = isLarge && isTablet ? ratios.R_38_BY_58 : ratios.XL;
  return isDesktop ? (
    <TeaserCard
      aria-hidden={isHidden}
      className={classnames(
        'tap-highlight-color-transparent transition-transform w-full duration-250 ease-in',
        className,
      )}
      isActive={isHovering}
      media={media}
      nextLabel={t('common.next')}
      previousLabel={t('common.previous')}
      ratio={ratio}
      theme="short"
    >
      <TagName
        {...props}
        className="text-initial flex w-full flex-col items-stretch gap-10"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        tabIndex={isHidden ? -1 : 0}
      >
        {content}
      </TagName>
    </TeaserCard>
  ) : (
    <TagName
      {...props}
      aria-hidden={isHidden}
      className={classnames(
        'tap-highlight-color-transparent text-initial block h-full w-full',
        className,
      )}
      tabIndex={isHidden ? -1 : 0}
    >
      <TeaserCard media={media.slice(0, 1)} ratio={ratio} theme="short">
        {content}
      </TeaserCard>
    </TagName>
  );
};
